import {
  BankOutlined,
  MenuOutlined,
  ClockCircleOutlined,
  CloudUploadOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  PushpinFilled,
  PushpinOutlined,
} from "@ant-design/icons";
import CajaStatusIndicator from "./components/Layout/CajaStatusIndicator";
import AvisoDeHuso from "./components/Layout/AvisoDeHuso";
import LogoExcelia from "./components/Layout/LogoExcelia";
import BuscadorGlobal from "./components/Layout/BuscadorGlobal";
import SyncStatusIndicator from "./components/Layout/SyncStatusIndicator";
import CriticalErrorModal from "./components/Layout/CriticalErrorModal";
import PageSpinner from "./components/Spinner/PageSpinner";
import {
  ConfigProvider,
  Layout,
  Menu,
  Button,
  Tag,
  Tooltip,
  Dropdown,
  Spin,
  Input,
  Empty,
  Alert,
  theme,
} from "antd";
import esES from "antd/lib/locale/es_ES";
import { COUNTRIES } from "./constants/countries";
import { useEffect, useState, ReactNode, useMemo, lazy, Suspense } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";
import "./App.css";
import PrivateRoute from "./components/Auth/PrivateRoute";
import Dashboard from "./components/Dashboard/Dashboard";
import UserMenu from "./components/Layout/UserMenu";
import EntornoActivo from "./components/Layout/EntornoActivo";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppConfigProvider, useConfig } from "./contexts/ConfigContext";
import { ProveedorDeTema, useTema } from "./contexts/TemaContext";
import { getBarraFijada, setBarraFijada } from "./services/barraLateralService";
import { clavesDeLaRuta } from "./config/menu/rutaDelMenu";
import { buscarEnElMenu } from "./config/menu/buscadorDelMenu";
import { contarPendientesDeSincronizar } from "./services/invoiceService";
import { useIsMobile } from "./hooks/useIsMobile";
import LoginPage from "./pages/LoginPage";
import {
  clearSync,
  getSyncConfigStorageKey,
  setSyncUserContext,
  setupSync,
} from "./services/syncService";
import { EXPIRATION_DATE } from "./constants";
import packageInfo from "../package.json";
import NotificationManager from "./components/NotificationManager";
import { getMenuItems } from "./config/menu";
import { canAccessRoute } from "./config/rbac/rbac.config";
import { recoverInterruptedInvoices } from "./services/invoiceRecoveryService";
import { activarModalesArrastrables } from "./hooks/modalesArrastrables";

// Componente LoginPage modificado con verificación de licencia
const LicenseProtectedLoginPage = (props: any) => {
  useEffect(() => {
    if (isLicenseExpired()) {
      showExpiredLicenseModal();
    }
  }, []);

  return <LoginPage {...props} />;
};

// Cada pantalla se descarga cuando se entra a ella, no al abrir la aplicación.
//
// Antes el paquete de arranque traía las treinta y tantas: quien abría la
// pantalla de acceso descargaba Informes, Configuración, Mantenimiento, Cupones
// y Campañas para poder escribir su usuario. Medido, el código propio del
// arranque eran 855 KB, y la mitad correspondía a pantallas que un turno entero
// puede no tocar.
//
// Dos quedan fuera de esta lista a propósito:
//
// - **La pantalla de acceso** es la primera que se dibuja; diferirla sería
//   cambiar tiempo de descarga por un parpadeo antes de poder escribir.
// - **El inicio** es donde aterriza todo el mundo al entrar.
//
// Facturación sí se difiere aunque sea la más pesada (209 KB) y la más usada:
// con el prefetch llega antes de que nadie la pida, y sacarla del arranque es
// justamente lo que más pesa.
//
// El `webpackPrefetch` va en todas por la misma razón que en las librerías: el
// service worker sólo pre-cachea el armazón, no los trozos de JavaScript. Sin
// él, una caja que no visitó una pantalla estando en línea se la encontraría
// vacía al perder la conexión. Con él, el navegador las baja cuando está
// ocioso — fuera del camino de arranque, pero presentes.
const AperturaDeCajaManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/AperturaDeCaja/AperturaDeCajaManagement")
);
const CajaManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/Caja/CajaManagement")
);
const CampaignsManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/Campaigns/CampaignsManagement")
);
const CierreDeCajaManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/CierreDeCaja/CierreDeCajaManagement")
);
const ResumenDelTurno = lazy(
  () => import(/* webpackPrefetch: true */ "./components/ResumenDelTurno/ResumenDelTurno")
);
const ClientManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/Client/ClientManagement")
);
const ColegioManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/Colegio/ColegioManagement")
);
const CouponsManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/Coupons/CouponsManagement")
);
const FormaDePagoManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/FormaDePago/FormaDePagoManagement")
);
const InvoiceManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/Invoice/InvoiceManagement")
);
const ProductManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/Product/ProductManagement")
);
const ProfilePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ProfilePage"));
const ReleaseNotesPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ReleaseNotesPage"));
const Reports = lazy(() => import(/* webpackPrefetch: true */ "./components/reports/Reports"));
const SalesPriceManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/SalesPrice/SalesPriceManagement")
);
const SettingsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/SettingsPage"));
const StockQuery = lazy(() =>
  import(/* webpackPrefetch: true */ "./components/Stock").then((m) => ({ default: m.StockQuery }))
);
const TPVManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/TPV/TPVManagement")
);
const TurnoManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/Turno/TurnoManagement")
);
const UserManagement = lazy(
  () => import(/* webpackPrefetch: true */ "./components/User/UserManagement")
);
const VendorManagement = lazy(() => import(/* webpackPrefetch: true */ "./components/Vendor"));

export interface AppRoute {
  path: string;
  element: ReactNode;
  private?: boolean;
}

export const routes: AppRoute[] = [
  // Public
  { path: "/login", element: <LicenseProtectedLoginPage /> },

  // Dashboard
  { path: "/", element: <Dashboard />, private: true },
  { path: "/maestros", element: <Dashboard />, private: true },
  { path: "/gestion", element: <Dashboard />, private: true },

  // Admin
  {
    path: "/usuarios",
    element: <UserManagement />,
    private: true,
  },
  {
    path: "/clientes",
    element: <ClientManagement />,
    private: true,
  },
  {
    path: "/productos",
    element: <ProductManagement />,
    private: true,
  },
  {
    path: "/formas-pago",
    element: <FormaDePagoManagement />,
    private: true,
  },
  {
    path: "/colegios",
    element: <ColegioManagement />,
    private: true,
  },
  { path: "/tpvs", element: <TPVManagement />, private: true },
  {
    path: "/precios-venta",
    element: <SalesPriceManagement />,
    private: true,
  },
  {
    path: "/cupones",
    element: <CouponsManagement />,
    private: true,
  },
  {
    path: "/campanas",
    element: <CampaignsManagement />,
    private: true,
  },
  {
    path: "/vendedores",
    element: <VendorManagement />,
    private: true,
  },

  // Authenticated
  // Las tres montan el MISMO componente, y por eso llevan `key`.
  //
  // Sin ella, React reconcilia por tipo y posición: al pasar de Facturas a
  // Abonos no se monta nada nuevo, sólo cambian las props, y el estado de la
  // pantalla anterior sigue ahí --incluido el listado ya cargado--. El síntoma
  // era que un abono recién creado llevaba a "Abonos de hoy" y se veían las
  // facturas; salir y volver a entrar lo arreglaba, porque ahí sí se montaba de
  // cero. Con la clave, cada ruta es una pantalla distinta también para React.
  {
    path: "/facturas",
    element: <InvoiceManagement key="facturas" documentType="Invoice" />,
    private: true,
  },
  {
    path: "/abonos",
    element: <InvoiceManagement key="abonos" documentType="Credit Memo" />,
    private: true,
  },
  {
    path: "/facturas-pendientes",
    element: <InvoiceManagement key="facturas-pendientes" syncStatus="pending" />,
    private: true,
  },
  { path: "/turnos", element: <TurnoManagement />, private: true },
  {
    path: "/aperturas-caja",
    element: <AperturaDeCajaManagement />,
    private: true,
  },
  { path: "/cajas", element: <CajaManagement />, private: true },
  { path: "/cierres-caja", element: <CierreDeCajaManagement />, private: true },
  { path: "/como-va-el-turno", element: <ResumenDelTurno />, private: true },
  { path: "/perfil", element: <ProfilePage />, private: true },
  { path: "/configuracion", element: <SettingsPage />, private: true },
  { path: "/notas-version", element: <ReleaseNotesPage />, private: true },
  { path: "/consulta-stock", element: <StockQuery />, private: true },
  { path: "/informes", element: <Reports />, private: true },
];

const { Header, Content, Sider } = Layout;

// Lo que se ve mientras llega el trozo de una pantalla diferida.
//
// Es el MISMO componente que usan las pantallas para su propia carga. Con un
// spinner distinto acá, entrar a una pantalla lenta mostraba dos avisos
// seguidos de distinto tamaño y en distinto sitio: primero el de la ruta,
// después el de la pantalla. Se ve como un salto, y no hay ninguna razón para
// que el usuario distinga entre "llegando el código" y "llegando los datos".
const CARGANDO_PANTALLA = <PageSpinner />;

// Componente para mostrar el estado de conexión
const ConnectionStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      (window as any).messageApi.success("Conexión restablecida. Sincronizando datos...", 3);
    };

    const handleOffline = () => {
      setIsOnline(false);
      (window as any).messageApi.info("Sin conexión. Trabajando en modo offline", 3);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (!isOnline) {
    return (
      <div
        style={{
          background: "#ffffb8",
          padding: "5px 10px",
          textAlign: "center",
          fontSize: "12px",
          width: "100%",
          position: "fixed",
          top: 0,
          zIndex: 1000,
        }}
      >
        Modo offline - Los cambios se sincronizarán cuando recuperes la conexión
      </div>
    );
  }

  return null;
};

// Componente para mostrar el tiempo restante de licencia
const LicenseCountdown = () => {
  const { logout } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const expirationDate = new Date(
        +EXPIRATION_DATE.split("/")[2],
        +EXPIRATION_DATE.split("/")[1] - 1,
        +EXPIRATION_DATE.split("/")[0],
        23,
        59,
        59
      );
      const now = new Date();
      const difference = expirationDate.getTime() - now.getTime();

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    };

    setTimeRemaining(calculateTimeRemaining());

    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);

    return () => clearInterval(timer);
  }, [logout]);

  // Si la licencia ha expirado, mostrar mensaje pero no cerrar sesión
  useEffect(() => {
    if (isExpired) {
      (window as any).messageApi.warning(
        "Su licencia ha expirado. Por favor contacte con el administrador.",
        3
      );
    }
  }, [isExpired]);

  const getTagColor = () => {
    if (timeRemaining.days > 30) return "success";
    if (timeRemaining.days > 7) return "warning";
    return "error";
  };

  return (
    <Tooltip title="Tiempo restante de licencia">
      <Tag
        color={getTagColor()}
        icon={<ClockCircleOutlined />}
        style={{
          fontSize: isMobile ? "11px" : "12px",
          padding: isMobile ? "2px 6px" : "0 8px",
          margin: 0,
          whiteSpace: "nowrap",
        }}
      >
        {isMobile
          ? `${timeRemaining.days}d`
          : `Licencia: ${timeRemaining.days}d ${timeRemaining.hours}h ${timeRemaining.minutes}m`}
      </Tag>
    </Tooltip>
  );
};

/**
 * Los documentos que quedaron sin viajar, en la cabecera.
 *
 * El aviso también está en el menú, junto a su pantalla, pero ahí no basta: la
 * barra está oculta por omisión y el ítem vive dentro de un submenú, así que
 * habría que abrir el menú y desplegar «Gestión» para enterarse de algo que uno
 * no sabe que tiene que mirar. Acá está siempre a la vista, como la caja y la
 * licencia, y lleva a su pantalla de un clic.
 *
 * **Se ve siempre, también en cero.** Al principio se ocultaba cuando no había
 * nada, para no acostumbrar a nadie a un aviso que nunca dice nada. Salió mal
 * en la primera prueba: mirando la cabecera vacía no hay forma de distinguir
 * «no hay nada pendiente» de «esto no funciona», y eso es peor que el ruido de
 * un cero. Sus dos vecinos ya lo hacían bien --«Sin caja abierta» también se
 * muestra sin caja--: son indicadores de estado, y el color lleva el mensaje.
 */
const AvisoDePendientes = ({ cuantos }: { cuantos: number }) => {
  const navigate = useNavigate();
  const hayPendientes = cuantos > 0;

  return (
    <Tooltip
      title={
        hayPendientes
          ? "Documentos que todavía no han llegado a Business Central"
          : "Todo lo emitido ha llegado a Business Central"
      }
    >
      <Tag
        icon={hayPendientes ? <CloudUploadOutlined /> : <CheckCircleOutlined />}
        color={hayPendientes ? "warning" : "success"}
        onClick={() => navigate("/facturas-pendientes")}
        style={{ cursor: "pointer", margin: 0, whiteSpace: "nowrap" }}
      >
        {hayPendientes ? `${cuantos} sin enviar` : "Todo enviado"}
      </Tag>
    </Tooltip>
  );
};

// Función para verificar si la licencia ha expirado
const isLicenseExpired = () => {
  const expirationDate = new Date(
    +EXPIRATION_DATE.split("/")[2],
    +EXPIRATION_DATE.split("/")[1] - 1,
    +EXPIRATION_DATE.split("/")[0],
    23,
    59,
    59
  );
  return new Date() > expirationDate;
};

// Aviso de licencia expirada, vía NotificationManager (window.messageApi) en
// vez de un <Modal> propio -- guard a nivel de módulo (no estado de React)
// porque se dispara desde dos componentes distintos (login y app autenticada)
// con varios efectos cada uno; sin el guard, cada uno crearía su propia
// instancia y se apilarían modales duplicados.
let expiredLicenseModalOpen = false;

const showExpiredLicenseModal = () => {
  if (expiredLicenseModalOpen) return;
  expiredLicenseModalOpen = true;

  (window as any).messageApi.warning(
    <div style={{ padding: "20px 0", textAlign: "center" }}>
      <p style={{ fontSize: "16px", marginBottom: "16px" }}>
        Su licencia de ExceliaOne ha expirado el <strong>{EXPIRATION_DATE}</strong>.
      </p>
      <p style={{ fontSize: "14px", color: "#666" }}>
        Por favor contacte con el administrador para renovar su licencia.
      </p>
      <p style={{ fontSize: "14px", color: "#666", marginTop: "16px" }}>
        Pulse "Aceptar para continuar" para seguir usando la aplicación.
      </p>
    </div>,
    {
      modal: true,
      title: "⚠️ Licencia Expirada",
      closable: false,
      maskClosable: false,
      keyboard: false,
      okText: "Aceptar para continuar",
      cancelButtonProps: { style: { display: "none" } },
      centered: true,
      width: 400,
      onOk: () => {
        expiredLicenseModalOpen = false;
      },
    }
  );
};

// Componente interno que usa useAuth
const AppContent = () => {
  const { token } = theme.useToken();
  const { isAuthenticated, loading: authLoading, currentUser } = useAuth() as any;
  const { country, loading: configLoading, activeEnv } = useConfig();
  const isUserAuthenticated = isAuthenticated();

  // Un país que el terminal no conoce se queda sin `country` para siempre, y
  // el velo de «Cargando configuración del país» giraba sin fin y sin decir
  // nada: no había forma de distinguir «está cargando» de «este país no
  // existe acá». Pasó al apuntar el entorno a Panamá, que no estaba en el
  // catálogo. Ahora la carga termina y el motivo se dice.
  const paisDesconocido = isUserAuthenticated && !configLoading && !country;
  const shouldShowOverlay = authLoading || (isUserAuthenticated && configLoading);

  // La barra lateral: oculta por omisión y encima del contenido, salvo que se
  // clave con el alfiler.
  //
  // `fijada` es la preferencia --se guarda--; `collapsed` es si ahora mismo se
  // ve. No son lo mismo: una barra sin fijar puede estar abierta un momento, y
  // una fijada se puede plegar sin perder el alfiler.
  const { pathname } = useLocation();
  const navegar = useNavigate();
  const [pendientes, setPendientes] = useState(0);
  const [filtroDelMenu, setFiltroDelMenu] = useState("");
  const [fijada, setFijada] = useState(getBarraFijada);
  const [collapsed, setCollapsed] = useState(() => !getBarraFijada());
  const isMobile = useIsMobile();

  // Las ventanas modales se pueden arrastrar por su cabecera. Se engancha una
  // sola vez y vale para las 37 que hay y para las que vengan; ver
  // hooks/modalesArrastrables.js.
  useEffect(() => activarModalesArrastrables(), []);

  // Las dos formas de estar abierta, y son excluyentes:
  //
  // - **Empujando**: fijada con el alfiler en pantalla grande. El contenido se
  //   corre 200 px a la derecha, como toda la vida. No hay velo: se puede
  //   trabajar con la barra a la vista.
  // - **Flotando**: abierta sin fijar, o en móvil. Va **encima** del contenido,
  //   que conserva todo el ancho, con un velo detrás que la cierra al tocarlo.
  //
  // En móvil nunca empuja aunque esté fijada: no hay ancho que repartir.
  const barraEmpuja = isUserAuthenticated && !isMobile && fijada && !collapsed;
  const barraFlotando = isUserAuthenticated && !collapsed && !barraEmpuja;

  // Una barra que flota se va sola: al tocar fuera y al elegir una pantalla.
  // Si está fijada, no --justamente para eso se fija--.
  const cerrarSiFlota = () => {
    if (barraFlotando) setCollapsed(true);
  };

  // Ir a una pantalla desde el buscador. Se navega y se cierra igual que si se
  // hubiera elegido del menú, porque es lo mismo.
  const irAPantalla = (destino: string) => {
    setFiltroDelMenu("");
    navegar(destino);
    cerrarSiFlota();
  };

  const alternarFijada = () => {
    const nueva = !fijada;
    setFijada(nueva);
    setBarraFijada(nueva);
    // Clavar el alfiler la abre; soltarlo la deja abierta pero flotando, para
    // que se vea qué cambió en vez de que desaparezca de golpe.
    if (nueva) setCollapsed(false);
  };

  const menuItems = useMemo(
    () =>
      getMenuItems({
        isAdmin: currentUser?.Admin || false,
        country,
        pendientes,
      }),
    [currentUser, country, pendientes]
  );

  // Dónde estamos, según el propio menú.
  const { seleccionadas, abiertas } = useMemo(
    () => clavesDeLaRuta(menuItems, pathname),
    [menuItems, pathname]
  );

  // Con algo escrito, el menú deja de ser un árbol y pasa a ser la lista de lo
  // que coincide. Quien busca quiere el destino, no navegar hasta él.
  const resultados = useMemo(
    () => buscarEnElMenu(menuItems, filtroDelMenu),
    [menuItems, filtroDelMenu]
  );
  const buscando = filtroDelMenu.trim().length > 0;

  // Lo escrito no sobrevive a cerrar la barra: al volver a abrirla se espera el
  // menú entero, no el resultado de una búsqueda de hace media hora.
  useEffect(() => {
    if (collapsed) setFiltroDelMenu("");
  }, [collapsed]);

  // Qué submenús están desplegados.
  //
  // Se controla desde acá --y no se deja a la librería-- por dos razones: al
  // plegar la barra a 0 la librería olvida lo que estaba abierto, así que había
  // que volver a desplegar «Gestión» cada vez para llegar a Facturas; y así el
  // submenú de la pantalla actual llega abierto.
  const [desplegados, setDesplegados] = useState(abiertas);

  // Al cambiar de pantalla se **añade** el submenú que toca, no se reemplaza la
  // lista: si alguien dejó otro abierto para mirar, no se le cierra en la cara.
  useEffect(() => {
    if (!abiertas.length) return;
    setDesplegados((previos) =>
      abiertas.every((clave) => previos.includes(clave))
        ? previos
        : [...new Set([...previos, ...abiertas])]
    );
  }, [abiertas]);

  // El número de documentos sin enviar, al arrancar y cuando termina una
  // sincronización. También al sacar el menú, que es el único momento en que se
  // ve: si algo cambió mientras estaba oculto, se entera al abrirlo.
  useEffect(() => {
    if (!isUserAuthenticated) return undefined;

    let vigente = true;
    const contar = async () => {
      const cuantos = await contarPendientesDeSincronizar();
      if (vigente) setPendientes(cuantos);
    };

    contar();
    const alSincronizar = (evento: Event) => {
      if ((evento as CustomEvent).detail?.isComplete) contar();
    };
    window.addEventListener("syncStatus", alSincronizar);
    return () => {
      vigente = false;
      window.removeEventListener("syncStatus", alSincronizar);
    };
  }, [isUserAuthenticated, pathname, collapsed]);

  // Verificar licencia expirada al cargar la aplicación
  useEffect(() => {
    if (isLicenseExpired() && isAuthenticated()) {
      showExpiredLicenseModal();
    }
  }, [isAuthenticated]);

  // Detectar recarga de página (F5) y mostrar modal si la licencia está expirada
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Marcar que la página se está recargando
      sessionStorage.setItem("pageReloading", "true");
    };

    const handleLoad = () => {
      // Si la página se recargó y la licencia está expirada, mostrar modal
      if (
        sessionStorage.getItem("pageReloading") === "true" &&
        isLicenseExpired() &&
        isAuthenticated()
      ) {
        showExpiredLicenseModal();
        sessionStorage.removeItem("pageReloading");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("load", handleLoad);

    // Verificar al montar el componente si es una recarga
    if (isLicenseExpired() && isAuthenticated()) {
      showExpiredLicenseModal();
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("load", handleLoad);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      recoverInterruptedInvoices();
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!currentUser) {
      clearSync();
      setSyncUserContext(null);
    } else {
      setSyncUserContext(currentUser);
    }

    let cancelled = false;
    (async () => {
      try {
        const config = localStorage.getItem(getSyncConfigStorageKey());
        await setupSync(config ? JSON.parse(config) : null, { isUserAuthenticated });
      } catch (error) {
        console.error("Error al inicializar servicio de sincronización:", error);
        if (!cancelled) {
          (window as any).messageApi.warning(
            "El servicio de sincronización no pudo inicializarse correctamente. Algunas funciones pueden no estar disponibles."
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Depende del *id* del usuario y no del objeto entero, a propósito:
    // `setupSync` reconfigura los temporizadores de sincronización, y el objeto
    // de AuthContext cambia de identidad cada vez que ese contexto se actualiza
    // aunque siga siendo la misma persona. Con el objeto en las dependencias,
    // la sincronización se rearmaría sola cada tanto sin que nadie cambiara de
    // usuario. `isUserAuthenticated` se deriva de `currentUser`, así que ya está
    // cubierto por el mismo corte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, currentUser?.id]);

  // Si el usuario no está autenticado, mostrar solo la página de login
  if (!isUserAuthenticated) {
    return (
      <Routes>
        <Route path="*" element={<LicenseProtectedLoginPage />} />
      </Routes>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <ConnectionStatus />

      {/* El velo detrás de una barra que flota. Antes era sólo de móvil; ahora
          también en escritorio, porque en escritorio la barra también se pone
          encima del contenido mientras no esté fijada. */}
      {barraFlotando && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            zIndex: 998,
          }}
          onClick={() => setCollapsed(true)}
        />
      )}

      {isUserAuthenticated && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value, tipo) => {
            // El cambio por tamaño de ventana sólo puede CERRAR. Con
            // `breakpoint="lg"`, la librería también avisa al ensanchar, y
            // hacerle caso abriría el menú encima del contenido cada vez que
            // alguien agranda la ventana. Si está fijada sí se le hace caso:
            // ahí el menú es parte de la pantalla, no algo que aparece encima.
            if (tipo === "responsive" && !value && !fijada) return;
            setCollapsed(value);
          }}
          width={200}
          style={{
            overflow: "auto",
            height: "100vh",
            position: "fixed",
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 999,
          }}
          theme="dark"
          breakpoint="lg"
          // Plegada mide 0, también en escritorio: la idea es que el ancho
          // entero sea para el contenido. El riel de 80 px de antes no servía
          // para leer un menú ni dejaba trabajar el listado.
          collapsedWidth={0}
          // Sin la manija de la librería: ya hay dos formas de manejarla --las
          // tres rayas de la cabecera y el alfiler-- y una tercera pegada al
          // borde sobraba.
          trigger={null}
        >
          {/* El contenido de la barra sólo existe mientras se ve.
              Plegada mide 0, pero la librería la seguía considerando «colapsada»
              y eso cambia el menú de modo: los submenús dejaban de desplegarse
              hacia abajo y salían como carteles flotantes sobre el contenido,
              sin barra debajo que los explicara. Además, al colapsar avisaba de
              que se habían cerrado todos los submenús y eso borraba lo que
              teníamos guardado, así que al reabrir el menú aparecía "reiniciado".
              Desmontándolo, ninguna de las dos cosas puede pasar. */}
          {!collapsed && (
            <>
              <div
                className="logo"
                style={{
                  // `width: auto` pisa el `width: 120px` de la clase `.logo` en
                  // App.css. Ese ancho alcanzaba justo para el texto solo; con el
                  // isotipo delante, «ExceliaOne» dejaba de caber y el `overflow:
                  // hidden` se comía la última letra --se leía «ExceliaOn»--. El
                  // ancho lo pone ahora la barra lateral: 200 menos los márgenes.
                  width: "auto",
                  height: "32px",
                  margin: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "10px",
                  color: "white",
                  fontSize: "18px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                <Tooltip title="Ir al inicio" placement="right">
                  <Link
                    to="/"
                    onClick={cerrarSiFlota}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "10px",
                      color: "inherit",
                      textDecoration: "none",
                    }}
                  >
                    <LogoExcelia size={20} />
                    ExceliaOne
                  </Link>
                </Tooltip>
                {/* El alfiler: clava la barra para que empuje el contenido en vez
                de flotar sobre él. En móvil no aparece porque ahí no hay ancho
                que repartir: siempre flota. */}
                {!isMobile && (
                  <Tooltip
                    title={fijada ? "Soltar la barra: se ocultará sola" : "Fijar la barra"}
                    placement="right"
                  >
                    <Button
                      type="text"
                      size="small"
                      aria-label={fijada ? "Soltar la barra lateral" : "Fijar la barra lateral"}
                      onClick={alternarFijada}
                      icon={fijada ? <PushpinFilled /> : <PushpinOutlined />}
                      style={{
                        marginLeft: "auto",
                        color: fijada ? "#1677ff" : "rgba(255,255,255,.55)",
                      }}
                    />
                  </Tooltip>
                )}
              </div>
              <Input
                allowClear
                size="small"
                prefix={<SearchOutlined style={{ color: "rgba(255,255,255,.45)" }} />}
                placeholder="Buscar pantalla"
                value={filtroDelMenu}
                onChange={(e) => setFiltroDelMenu(e.target.value)}
                onKeyDown={(e) => {
                  // Enter va al primer resultado: quien busca deprisa escribe
                  // tres letras y pulsa Enter sin llegar a mirar la lista.
                  if (e.key === "Enter" && resultados[0]) irAPantalla(resultados[0].destino);
                  // Esc limpia lo escrito antes que cerrar la barra: cerrarla de
                  // golpe obligaría a abrirla otra vez para corregir una letra.
                  if (e.key === "Escape" && filtroDelMenu) {
                    e.stopPropagation();
                    setFiltroDelMenu("");
                  }
                }}
                style={{ margin: "0 16px 8px", width: "calc(100% - 32px)" }}
              />

              {buscando && resultados.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span style={{ color: "rgba(255,255,255,.45)", fontSize: 12 }}>
                      Ninguna pantalla se llama así
                    </span>
                  }
                  style={{ marginTop: 24 }}
                />
              )}

              <Menu
                theme="dark"
                mode="inline"
                // `selectedKeys` y no `defaultSelectedKeys`: el fijo era `["1"]`, así
                // que «Inicio» quedaba marcado en todas las pantallas.
                selectedKeys={seleccionadas}
                openKeys={desplegados}
                onOpenChange={(claves) => {
                  // Sólo se le hace caso a la barra visible. Al plegarse, la
                  // librería avisa de que ha cerrado todos los submenús, y ese
                  // aviso borraba lo guardado: al reabrir aparecía reiniciado.
                  if (!collapsed) setDesplegados(claves as string[]);
                }}
                items={
                  buscando
                    ? (resultados.map((r) => ({
                        key: r.key,
                        label: (
                          <span>
                            {r.titulo}
                            {r.padre && (
                              <span style={{ opacity: 0.45, fontSize: 11, marginLeft: 6 }}>
                                {r.padre}
                              </span>
                            )}
                          </span>
                        ),
                        onClick: () => irAPantalla(r.destino),
                      })) as any)
                    : (menuItems as any)
                }
                onClick={buscando ? undefined : cerrarSiFlota}
                style={{
                  border: "none",
                  height: "calc(100vh - 120px)", // Dejar espacio para logo (64px) y versión (56px)
                  overflow: "auto",
                }}
              />
              {/* Mostrar versión en la parte inferior del menú */}
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: "12px 16px",
                  borderTop: "1px solid #434343",
                  background: "#001529",
                  color: "#8c8c8c",
                  fontSize: "11px",
                  textAlign: "center",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <div>
                  <div style={{ fontWeight: "500", fontSize: "12px", color: "white" }}>
                    {country?.name || ""}
                  </div>
                  <div style={{ marginTop: "2px", opacity: 0.8, fontSize: "10px" }}>
                    v{packageInfo.version}
                  </div>
                </div>
              </div>
            </>
          )}
        </Sider>
      )}

      <Layout
        style={{
          // Sólo se corre cuando la barra está fijada. Flotando va encima, así
          // que el contenido conserva el ancho entero de la ventana.
          marginLeft: barraEmpuja ? 200 : 0,
          transition: "margin-left .2s",
        }}
      >
        {isUserAuthenticated && (
          <Header
            style={{
              padding: "0 8px",
              background: "#001529",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
              position: "sticky",
              top: 0,
              zIndex: 998,
              width: "100%",
              color: "white",
              height: isMobile ? "48px" : "64px",
              minHeight: isMobile ? "48px" : "64px",
            }}
          >
            {/* Las tres rayas. Ya no son sólo de móvil: en escritorio son la
                forma normal de sacar el menú, porque por omisión está oculto.
                Cuando la barra está fijada no hacen falta --se ve entera, y el
                alfiler la suelta--, así que dejan su hueco. */}
            <div style={{ display: "flex", alignItems: "center" }}>
              {!barraEmpuja ? (
                <Tooltip title="Menú" placement="bottomLeft">
                  <Button
                    type="text"
                    aria-label="Abrir el menú"
                    icon={<MenuOutlined />}
                    onClick={() => setCollapsed(!collapsed)}
                    style={{
                      fontSize: "16px",
                      width: 32,
                      height: 32,
                      color: "white",
                      padding: 0,
                    }}
                  />
                </Tooltip>
              ) : (
                <div style={{ width: 32 }}></div>
              )}

              {/* Solo mostrar este título en escritorio o hacer que sea mucho más pequeño en móvil */}
              {/* La marca vive en la barra lateral... salvo cuando está oculta, que
                  es lo normal ahora. Entonces pasa a la cabecera: si no, el
                  terminal se quedaría sin su nombre en pantalla. */}
              {!barraEmpuja && (
                <Tooltip title="Ir al inicio" placement="bottomLeft">
                  <Link
                    to="/"
                    style={{
                      marginLeft: 16,
                      fontSize: 16,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      color: "inherit",
                      textDecoration: "none",
                    }}
                  >
                    <LogoExcelia size={16} />
                    ExceliaOne
                  </Link>
                </Tooltip>
              )}
            </div>

            {/* El buscador, en el centro y con todo el hueco que sobre.
                En móvil no cabe --la cabecera mide 48 px y ya lleva marca,
                estado y usuario--, así que ahí se deja fuera: la barra lateral
                tiene el suyo para las pantallas. */}
            {!isMobile && (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  padding: "0 16px",
                  minWidth: 0,
                }}
              >
                <BuscadorGlobal pantallas={menuItems} />
              </div>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              {isMobile ? (
                <Dropdown
                  dropdownRender={() => (
                    <div
                      style={{
                        background: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        borderRadius: "4px",
                        padding: "8px",
                        marginTop: "8px",
                        color: "black",
                      }}
                    >
                      <EntornoActivo />
                      <div style={{ marginTop: "8px" }}>
                        <CajaStatusIndicator />
                      </div>
                      <AvisoDeHuso enMenuMovil />
                      <div style={{ marginTop: "8px" }}>
                        <LicenseCountdown />
                      </div>
                      <div style={{ marginTop: "8px" }}>
                        <AvisoDePendientes cuantos={pendientes} />
                      </div>
                    </div>
                  )}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <Button
                    type="text"
                    icon={<BankOutlined />}
                    style={{
                      color: "white",
                      padding: 0,
                      width: 32,
                      height: 32,
                    }}
                  />
                </Dropdown>
              ) : (
                <>
                  <EntornoActivo />
                  <AvisoDePendientes cuantos={pendientes} />
                  <AvisoDeHuso />
                  <CajaStatusIndicator />
                  <LicenseCountdown />
                </>
              )}
              <UserMenu />
            </div>
          </Header>
        )}

        {paisDesconocido && (
          <Alert
            type="error"
            showIcon
            style={{ margin: isMobile ? "8px" : "16px" }}
            message={`El terminal no conoce el país «${activeEnv?.country || "(sin país)"}»`}
            description={
              <>
                No se puede operar hasta darlo de alta. Los países configurados son:{" "}
                {COUNTRIES.map((p) => `${p.name} (${p.code})`).join(", ")}.
              </>
            }
          />
        )}

        <Spin
          spinning={shouldShowOverlay}
          tip="Cargando configuración del país..."
          size="large"
          style={{ maxHeight: "100vh" }}
        >
          <Content
            style={{
              margin: isMobile ? "8px" : "16px",
              padding: isMobile ? "12px" : "24px",
              // Era `#fff` fijo. En oscuro pintaba de blanco toda la zona de
              // contenido: las tarjetas la tapaban con su propio fondo, pero lo
              // que no vive dentro de una tarjeta --el título de la pantalla, el
              // separador, los huecos entre tarjetas-- quedaba con letra blanca
              // sobre blanco. Era el «se pierde la cabecera».
              background: token.colorBgContainer,
              borderRadius: "8px",
              minHeight: 280,
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Routes>
              {routes.map(({ path, element, private: isPrivate }) => {
                if (!canAccessRoute(country?.code, currentUser?.Admin || false, path)) return null;

                const wrappedElement = isPrivate ? <PrivateRoute>{element}</PrivateRoute> : element;

                // El Suspense va por ruta y no alrededor de <Routes>: así el
                // aviso de carga reemplaza el contenido de la pantalla y no el
                // menú ni la cabecera, que ya están dibujados.
                return (
                  <Route
                    key={path}
                    path={path}
                    element={<Suspense fallback={CARGANDO_PANTALLA}>{wrappedElement}</Suspense>}
                  />
                );
              })}

              {/* Fallback */}
              <Route
                path="*"
                element={
                  <div style={{ textAlign: "center", padding: 50, color: "#ff4d4f" }}>
                    <h2>Acceso no autorizado</h2>
                    <p>No tienes permisos para acceder a esta sección.</p>
                    <p>Contacta con el administrador si necesitas acceso.</p>
                  </div>
                }
              />
            </Routes>
          </Content>
        </Spin>
      </Layout>
    </Layout>
  );
};

// La configuración de la librería de interfaz, que necesita saber el tema.
//
// Va en un componente aparte porque `useTema` sólo se puede leer por dentro del
// proveedor, y el `ConfigProvider` tiene que quedar por encima de todo lo demás
// para que el algoritmo alcance a cada componente que se dibuje.
const MarcoDeAntd = ({ children }: { children: ReactNode }) => {
  const { esOscuro } = useTema();

  return (
    <ConfigProvider
      locale={esES}
      theme={{
        algorithm: esOscuro ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: "#1677ff",
          colorSuccess: "#52c41a",
          colorWarning: "#faad14",
          fontSize: 14,
          borderRadius: 6,
          // En oscuro, los bordes que trae el algoritmo (#424242 y #303030) se
          // ven como un enrejado: en una tabla de veinte columnas hay más línea
          // que dato. Bajados un escalón, la rejilla se lee y deja de competir
          // con el contenido.
          ...(esOscuro ? { colorBorder: "#303030", colorBorderSecondary: "#262626" } : {}),
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

// Componente principal App que no usa useAuth directamente
function App() {
  return (
    <ProveedorDeTema>
      <MarcoDeAntd>
        <Router>
          <NotificationManager />
          <AuthProvider>
            <AppConfigProvider>
              <AppContent />
              {/* Indicadores y modal de error montados globalmente */}
              <SyncStatusIndicator />
              <CriticalErrorModal />
            </AppConfigProvider>
          </AuthProvider>
        </Router>
      </MarcoDeAntd>
    </ProveedorDeTema>
  );
}

export default App;
